import React, { useState } from 'react';

import type { FormInstance } from 'antd';

// What the sign-up forms share: a field label marked required, and the way a wrong field is pointed at.

// A field's label with a small asterisk after it, as PandaDoc marks the required fields.
export function RequiredLabel({ text }: { text: string }) {
    return (
        <>
            {text}
            <span style={{ marginLeft: 4, fontSize: 12, color: '#8c8c8c' }}>*</span>
        </>
    );
}

// The button pressed with something wrong: the first field to fix shakes (class field-shake, styles.less) and takes the
// focus, every press again. A field is checked when it is left, not on every key; once it shows an error, it is checked
// as it is fixed.
export function useFieldPointer(form: FormInstance) {
    const [shaking, setShaking] = useState<string | null>(null);

    const pointAt = (field: string) => {
        setShaking(null);
        requestAnimationFrame(() => setShaking(field));
        form.getFieldInstance(field)?.focus?.();
    };

    const recheckIfWrong = (field: string) => () => {
        if (form.getFieldError(field).length) form.validateFields([field]);
    };

    const shakeClass = (field: string) => (shaking === field ? 'field-shake' : undefined);

    return { pointAt, recheckIfWrong, shakeClass };
}

// A field checked by its length alone, with its hint always under it ("At least 8 characters"): grey, red once the
// field is left too short, grey again as soon as it is long enough. Checked by hand, not by antd's rules: a rule's
// message comes in antd's message area, which slides in and out and moves everything below.
export function useMinLength(min: number) {
    const [wrong, setWrong] = useState(false);
    const tooShort = (value?: string) => (value || '').length < min;

    return {
        wrong,
        setWrong,
        tooShort,
        onLeave: (value: string) => setWrong(tooShort(value)),
        onType: (value: string) => wrong && setWrong(tooShort(value)),
    };
}

// The hint under such a field: one line in one place, only its colour changes.
export function FieldHint({ wrong, children }: { wrong: boolean; children: React.ReactNode }) {
    return <div className={wrong ? 'field-hint field-hint--error' : 'field-hint'}>{children}</div>;
}
